import React, { useState, useEffect, useCallback } from 'react';
import { FaHistory, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { getAuditRuns } from '../services/auditService';

const STATUS_BADGE_CLASSES = {
  completed: 'bg-green-100 text-green-700',
  running: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  const parsed = new Date(timestamp);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatWhen = (timestamp) => {
  const date = toDate(timestamp);
  return date
    ? date.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', hour12: true })
    : 'Unknown time';
};

/**
 * Collapsible, read-only list of past bulk-run summaries (auditRuns docs) for one operation.
 * Fetches lazily via the generic /audit/runs endpoint. Collapsed by default.
 */
const AuditRunsList = ({ operation, title = 'Recent Runs', limit = 10 }) => {
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAuditRuns({ operation, limit });
      // makeAuthenticatedRequest already unwraps the {status, data} envelope; tolerate both shapes.
      setRuns(res?.runs || res?.data?.runs || []);
    } catch (err) {
      console.error(`[AuditRunsList] Failed to fetch runs for ${operation}:`, err);
      setError('Could not load run history.');
      setRuns([]);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [operation, limit]);

  useEffect(() => {
    if (expanded && !fetched) fetchRuns();
  }, [expanded, fetched, fetchRuns]);

  return (
    <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <FaHistory className="mr-3 text-jims-cyan-dark text-base" />
          <span className="text-base font-semibold text-gray-700">{title}</span>
        </div>
        {expanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-6 pb-5 border-t border-gray-100">
          {error ? (
            <p className="text-red-500 text-sm py-4 text-center">{error}</p>
          ) : loading ? (
            <p className="text-gray-500 text-sm py-4 text-center">Loading run history...</p>
          ) : runs.length === 0 ? (
            <p className="text-gray-500 text-sm py-4 text-center">No runs recorded yet.</p>
          ) : (
            <div className="mt-3 divide-y divide-gray-100">
              {runs.map((run) => (
                <div key={run.id} className="py-3">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE_CLASSES[run.status] || 'bg-gray-100 text-gray-600'}`}>
                      {run.status || 'unknown'}
                    </span>
                    <span className="text-sm font-medium text-gray-800">{run.performedBy?.email || 'Unknown'}</span>
                    <span className="text-xs text-gray-400">{formatWhen(run.startedAt)}</span>
                  </div>
                  {run.message && <p className="text-xs text-gray-500">{run.message}</p>}
                  {run.error && <p className="text-xs text-red-500">{run.error}</p>}
                  {run.counts && Object.keys(run.counts).length > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {Object.entries(run.counts).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditRunsList;
